/**
 * useBulkActions Hook Template
 *
 * Manages bulk actions and selection state for tables
 * Provides optimized selection operations with Set<string>
 * Includes undo functionality and action confirmation
 */

"use client";

import { useState, useCallback, useRef, useMemo } from "react";

import toast from "react-hot-toast";

export interface BulkAction {
  id: string;
  label: string;
  icon?: React.ReactNode;
  color?:
    | "default"
    | "primary"
    | "secondary"
    | "success"
    | "warning"
    | "danger";
  confirmMessage?: string;
  requiresConfirmation?: boolean;
  disabled?: boolean;
  isDestructive?: boolean;
}

export interface BulkActionResult {
  success: boolean;
  message?: string;
  affectedItems?: string[];
  errors?: Record<string, string>;
}

export interface UseBulkActionsOptions<T = any> {
  /** All available items */
  items: T[];
  /** Function to extract unique key from item */
  getItemKey: (item: T) => string;
  /** Bulk action handlers */
  actions: Record<string, (selectedItems: T[]) => Promise<BulkActionResult>>;
  /** Enable undo functionality */
  enableUndo?: boolean;
  /** Undo timeout in milliseconds */
  undoTimeout?: number;
  /** Maximum selection limit */
  maxSelection?: number;
  /** Custom toast messages */
  toastMessages?: {
    success?: string;
    error?: string;
    maxSelectionReached?: string;
    noItemsSelected?: string;
  };
}

export interface UseBulkActionsReturn<T = any> {
  // Selection state
  selectedKeys: Set<string>;
  selectedItems: T[];
  isAllSelected: boolean;
  isIndeterminate: boolean;
  selectedCount: number;

  // Selection actions
  selectAll: () => void;
  selectNone: () => void;
  selectItem: (key: string) => void;
  unselectItem: (key: string) => void;
  toggleItem: (key: string) => void;
  toggleAll: () => void;
  setSelectedKeys: (keys: Set<string>) => void;

  // Bulk actions
  executeAction: (actionId: string) => Promise<void>;
  isActionDisabled: (actionId: string) => boolean;
  canExecuteAction: (actionId: string) => boolean;

  // Utility
  isSelected: (key: string) => boolean;
  getSelectableItems: () => T[];
  clearSelection: () => void;

  // Undo functionality
  undoLastAction: () => void;
  canUndo: boolean;
  undoActionLabel?: string;
}

export function useBulkActions<T = any>({
  items,
  getItemKey,
  actions,
  enableUndo = false,
  undoTimeout = 5000,
  maxSelection,
  toastMessages = {},
}: UseBulkActionsOptions<T>): UseBulkActionsReturn<T> {
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [lastActionState, setLastActionState] = useState<{
    actionId: string;
    previousSelection: Set<string>;
    affectedItems: string[];
    timestamp: number;
  } | null>(null);

  const undoTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Memoized computations
  const selectedItems = useMemo(
    () => items.filter((item) => selectedKeys.has(getItemKey(item))),
    [items, selectedKeys, getItemKey],
  );

  const isAllSelected = useMemo(
    () => items.length > 0 && selectedKeys.size === items.length,
    [items.length, selectedKeys.size],
  );

  const isIndeterminate = useMemo(
    () => selectedKeys.size > 0 && selectedKeys.size < items.length,
    [selectedKeys.size, items.length],
  );

  const selectedCount = selectedKeys.size;

  // Selection helpers
  const getSelectableItems = useCallback(() => items, [items]);

  const isSelected = useCallback(
    (key: string) => selectedKeys.has(key),
    [selectedKeys],
  );

  const clearSelection = useCallback(() => {
    setSelectedKeys(new Set());
  }, []);

  // Selection actions
  const selectAll = useCallback(() => {
    const allKeys = new Set(items.map(getItemKey));
    if (maxSelection && allKeys.size > maxSelection) {
      toast.error(
        toastMessages.maxSelectionReached ||
          `Maximum ${maxSelection} items can be selected`,
      );
      return;
    }
    setSelectedKeys(allKeys);
  }, [items, getItemKey, maxSelection, toastMessages.maxSelectionReached]);

  const selectNone = useCallback(() => {
    setSelectedKeys(new Set());
  }, []);

  const selectItem = useCallback(
    (key: string) => {
      setSelectedKeys((prev) => {
        const newSet = new Set(prev);
        if (maxSelection && newSet.size >= maxSelection && !newSet.has(key)) {
          toast.error(
            toastMessages.maxSelectionReached ||
              `Maximum ${maxSelection} items can be selected`,
          );
          return prev;
        }
        newSet.add(key);
        return newSet;
      });
    },
    [maxSelection, toastMessages.maxSelectionReached],
  );

  const unselectItem = useCallback((key: string) => {
    setSelectedKeys((prev) => {
      const newSet = new Set(prev);
      newSet.delete(key);
      return newSet;
    });
  }, []);

  const toggleItem = useCallback(
    (key: string) => {
      if (selectedKeys.has(key)) {
        unselectItem(key);
      } else {
        selectItem(key);
      }
    },
    [selectedKeys, selectItem, unselectItem],
  );

  const toggleAll = useCallback(() => {
    if (isAllSelected) {
      selectNone();
    } else {
      selectAll();
    }
  }, [isAllSelected, selectAll, selectNone]);

  // Action validation
  const isActionDisabled = useCallback(
    (actionId: string) => {
      return selectedKeys.size === 0 || !actions[actionId];
    },
    [selectedKeys.size, actions],
  );

  const canExecuteAction = useCallback(
    (actionId: string) => {
      return selectedKeys.size > 0 && !!actions[actionId];
    },
    [selectedKeys.size, actions],
  );

  // Execute bulk action
  const executeAction = useCallback(
    async (actionId: string) => {
      if (!canExecuteAction(actionId)) {
        toast.error(toastMessages.noItemsSelected || "No items selected");
        return;
      }

      const action = actions[actionId];
      if (!action) {
        toast.error("Action not found");
        return;
      }

      // Store state for undo
      const previousSelection = new Set(selectedKeys);

      try {
        const result = await action(selectedItems);

        if (result.success) {
          toast.success(
            result.message ||
              toastMessages.success ||
              "Action completed successfully",
          );

          // Store undo information
          if (enableUndo) {
            setLastActionState({
              actionId,
              previousSelection,
              affectedItems: result.affectedItems || Array.from(selectedKeys),
              timestamp: Date.now(),
            });

            // Clear undo state after timeout
            if (undoTimeoutRef.current) {
              clearTimeout(undoTimeoutRef.current);
            }

            undoTimeoutRef.current = setTimeout(() => {
              setLastActionState(null);
            }, undoTimeout);
          }

          // Clear selection after successful action
          clearSelection();
        } else {
          toast.error(result.message || toastMessages.error || "Action failed");
        }
      } catch (error) {
        console.error("Bulk action error:", error);
        toast.error(
          toastMessages.error || "An error occurred while executing the action",
        );
      }
    },
    [
      canExecuteAction,
      actions,
      selectedItems,
      selectedKeys,
      enableUndo,
      clearSelection,
      undoTimeout,
      toastMessages,
    ],
  );

  // Undo functionality
  const undoLastAction = useCallback(() => {
    if (!lastActionState || !enableUndo) return;

    const { previousSelection } = lastActionState;
    setSelectedKeys(new Set(previousSelection));
    setLastActionState(null);

    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
      undoTimeoutRef.current = null;
    }

    toast.success("Action undone");
  }, [lastActionState, enableUndo]);

  const canUndo = useMemo(
    () =>
      enableUndo &&
      lastActionState !== null &&
      Date.now() - lastActionState.timestamp < undoTimeout,
    [enableUndo, lastActionState, undoTimeout],
  );

  const undoActionLabel = lastActionState?.actionId;

  return {
    // Selection state
    selectedKeys,
    selectedItems,
    isAllSelected,
    isIndeterminate,
    selectedCount,

    // Selection actions
    selectAll,
    selectNone,
    selectItem,
    unselectItem,
    toggleItem,
    toggleAll,
    setSelectedKeys,

    // Bulk actions
    executeAction,
    isActionDisabled,
    canExecuteAction,

    // Utility
    isSelected,
    getSelectableItems,
    clearSelection,

    // Undo functionality
    undoLastAction,
    canUndo,
    undoActionLabel,
  };
}

export default useBulkActions;
